/**
 * IntelBadge — pokazuje SPHINX intel summary per entity (authority / contractor / supplier).
 *
 * Risk colors:
 *   green  = clean (no sanctions, low financial risk, active)
 *   amber  = moderate financial risk
 *   red    = sanctions match OR high financial risk
 *   unknown = no data yet (offer "Run intel" button)
 *
 * Tap badge → opens IntelDialog z pełnymi szczegółami z SPHINX.
 */

import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Shield, ShieldCheck, ShieldAlert, ShieldX, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

const RISK_CONFIG = {
  green:   { label: 'Verified',   color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: ShieldCheck },
  amber:   { label: 'Moderate',   color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: ShieldAlert },
  red:     { label: 'High Risk',  color: 'bg-red-500/15 text-red-300 border-red-500/30', icon: ShieldX },
  unknown: { label: 'No intel',   color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30', icon: Shield },
};

export default function IntelBadge({
  intel,         // intel_summary or authority_intel.summary object
  onEnrich,      // async fn: trigger SPHINX query
  lang = 'pl',
  compact = false,
  showDetails = true,
}) {
  const [open, setOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [detail, setDetail] = useState(null);

  const summary = intel?.summary || intel || {};
  const risk = summary.risk_level || 'unknown';
  const config = RISK_CONFIG[risk] || RISK_CONFIG.unknown;
  const Icon = config.icon;

  async function handleEnrich(e) {
    e?.stopPropagation();
    if (!onEnrich) return;
    setEnriching(true);
    try {
      const result = await onEnrich();
      setDetail(result);
      toast.success(t(lang, 'Intel pobrane z SPHINX', 'Intel fetched from SPHINX'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setEnriching(false);
    }
  }

  if (risk === 'unknown' && !summary.available) {
    return (
      <Button
        variant="ghost" size="sm"
        onClick={handleEnrich}
        disabled={enriching}
        className="gap-1 h-6 px-2 text-xs text-muted-foreground hover:text-primary"
        data-testid="intel-enrich"
      >
        {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
        {t(lang, 'Sprawdź wywiad', 'Run intel')}
      </Button>
    );
  }

  return (
    <>
      <Badge
        className={`${config.color} cursor-pointer text-[10px] gap-1 px-2`}
        onClick={() => showDetails && setOpen(true)}
        data-testid={`intel-badge-${risk}`}
        title={t(lang, 'Klik = szczegóły z SPHINX', 'Click for SPHINX details')}
      >
        <Icon className="w-3 h-3" />
        {config.label}
        {summary.sanctions_match && ' ⚠'}
      </Badge>

      {showDetails && (
        <IntelDialog
          open={open}
          onOpenChange={setOpen}
          summary={summary}
          detail={detail}
          onEnrich={handleEnrich}
          enriching={enriching}
          lang={lang}
        />
      )}
    </>
  );
}

function IntelDialog({ open, onOpenChange, summary, detail, onEnrich, enriching, lang }) {
  const formatPLN = (v) => v == null ? '—' : Number(v).toLocaleString('pl-PL', { maximumFractionDigits: 0 }) + ' PLN';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            {t(lang, 'Wywiad SPHINX', 'SPHINX Intelligence')}
            <Button size="sm" variant="ghost" onClick={onEnrich} disabled={enriching} className="ml-auto">
              {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {t(lang, 'Odśwież', 'Refresh')}
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Risk Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label={t(lang, 'Poziom ryzyka', 'Risk')} value={summary.risk_level || 'unknown'} color={summary.risk_level} />
            <Stat label={t(lang, 'Sankcje', 'Sanctions')} value={summary.sanctions_match ? '⚠ MATCH' : '✓ Clean'} color={summary.sanctions_match ? 'red' : 'green'} />
            <Stat label="Altman Z" value={summary.altman_z_score?.toFixed(2) || '—'} />
            <Stat label={t(lang, 'Limit kredytowy', 'Credit limit')} value={formatPLN(summary.credit_limit_pln)} />
          </div>

          {/* Sanctions details */}
          {summary.sanctions_match && summary.sanctions_details?.length > 0 && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
              <h4 className="text-sm font-medium text-red-300 mb-2">⚠ {t(lang, 'Trafienia sankcyjne', 'Sanctions matches')}:</h4>
              <ul className="text-xs space-y-1">
                {summary.sanctions_details.map((s, i) => (
                  <li key={i} className="text-red-200">{JSON.stringify(s)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Confidence */}
          <div className="text-xs text-muted-foreground">
            {t(lang, 'Pewność danych', 'Data confidence')}: {summary.confidence_pct || 0}% · {t(lang, 'Zarząd', 'Board')}: {summary.board_size || 0} {t(lang, 'osób', 'persons')}
          </div>

          {/* Full detail from latest enrich (if available) */}
          {detail?.full_response && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                {t(lang, 'Pełna odpowiedź SPHINX (debug)', 'Full SPHINX response (debug)')}
              </summary>
              <pre className="bg-muted/20 p-2 rounded mt-2 overflow-auto max-h-64 text-[10px]">
                {JSON.stringify(detail.full_response, null, 2)}
              </pre>
            </details>
          )}

          {summary.trace_id && (
            <div className="text-[10px] text-muted-foreground/60 text-right">
              SPHINX trace: {summary.trace_id}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color }) {
  const colorMap = {
    red: 'text-red-300',
    amber: 'text-amber-300',
    green: 'text-emerald-300',
    unknown: 'text-zinc-400',
  };
  return (
    <div className="p-2 bg-muted/20 rounded">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-medium mt-0.5 ${colorMap[color] || ''}`}>{value}</div>
    </div>
  );
}
